
import { GameState } from '../../GameContextTypes';
import { Skill } from '../../../types/game';

// Count skills by category
export const countSkillsByCategory = (skills: Skill[]) => {
  const skillCounts = {
    Technical: 0,
    Business: 0,
    Operations: 0,
    Leadership: 0
  };
  
  skills.forEach(skill => {
    if (skillCounts[skill.category] !== undefined) {
      skillCounts[skill.category]++;
    }
  });
  
  return skillCounts;
};

// Calculate bonus with diminishing returns based on skill count
export const getSkillCategoryBonus = (baseValue: number, count: number) => {
  return baseValue * Math.max(0.5, 1 / Math.sqrt(1 + count * 0.3));
};

// Apply effects based on skill categories
export const applySkillCategoryEffects = (
  kpis: GameState['kpis'], 
  skill: Skill, 
  skillCounts: Record<string, number>,
  careerMultiplier: number
) => {
  const updatedKpis = { ...kpis };
  
  switch(skill.category) {
    case 'Technical':
      const techBonus = getSkillCategoryBonus(0.2, skillCounts.Technical);
      updatedKpis.efficiency += techBonus * careerMultiplier;
      updatedKpis.uptime += getSkillCategoryBonus(0.15, skillCounts.Technical) * careerMultiplier;
      break;
    case 'Business':
      updatedKpis.profitMargin += getSkillCategoryBonus(0.25, skillCounts.Business) * careerMultiplier;
      break;
    case 'Operations':
      updatedKpis.energyCost = Math.max(0.03, updatedKpis.energyCost - 
        (getSkillCategoryBonus(0.001, skillCounts.Operations) * careerMultiplier));
      updatedKpis.efficiency += getSkillCategoryBonus(0.15, skillCounts.Operations) * careerMultiplier;
      break;
    case 'Leadership':
      updatedKpis.profitMargin += getSkillCategoryBonus(0.15, skillCounts.Leadership) * careerMultiplier;
      updatedKpis.uptime += getSkillCategoryBonus(0.1, skillCounts.Leadership) * careerMultiplier;
      break;
  }
  
  return updatedKpis;
};

// Apply effects for specific high-value skills
export const applySpecificSkillEffects = (
  kpis: GameState['kpis'],
  skillId: string,
  careerMultiplier: number
) => {
  const updatedKpis = { ...kpis };
  
  switch (skillId) {
    case 'cooling-optimization':
      updatedKpis.energyCost = Math.max(0.03, updatedKpis.energyCost - (0.002 * careerMultiplier));
      updatedKpis.efficiency += 0.15 * careerMultiplier;
      break;
    case 'firmware-tuning':
      updatedKpis.efficiency += 0.3 * careerMultiplier;
      break;
    case 'custom-firmware-development':
      updatedKpis.efficiency += 0.5 * careerMultiplier;
      break;
    case 'financial-planning':
      updatedKpis.profitMargin += 0.3 * careerMultiplier;
      break;
    case 'hedging-strategies':
      updatedKpis.profitMargin += 0.5 * careerMultiplier;
      break;
    case 'renewable-energy':
      updatedKpis.energyCost = Math.max(0.03, updatedKpis.energyCost - (0.004 * careerMultiplier));
      break;
    case 'disaster-recovery':
      updatedKpis.uptime += 0.4 * careerMultiplier;
      break;
    case 'strategic-planning':
      updatedKpis.profitMargin += 0.2 * careerMultiplier;
      updatedKpis.efficiency += 0.15 * careerMultiplier;
      updatedKpis.uptime += 0.15 * careerMultiplier;
      break;
    case 'industry-influence':
      updatedKpis.profitMargin += 0.5 * careerMultiplier;
      updatedKpis.energyCost = Math.max(0.03, updatedKpis.energyCost - (0.002 * careerMultiplier));
      break;
  }
  
  return updatedKpis;
};
